import { createContext, useContext, useState } from 'react';
import type { ReactNode } from 'react';

import type { InitialEnquiryModalValues } from 'types';
import type { ISearchPageAds } from 'api/types/searchPageApiTypes';

interface AdDetailsProviderProps {
  children: ReactNode;
  userSaved?: boolean;
}

type category = {
  id: number;
  description: string;
};

interface AdDetailsContextI {
  isReportAdModalOpen: boolean;
  setIsReportAdModalOpen: (t: boolean) => void;
  adId: number;
  setAdId: (t: number) => void;
  categories?: category[];
  setCategories: (t: category[]) => void;
  currentReportAdView: 'INITIAL' | 'SUCCESS';
  setCurrentReportAdView: (t: 'INITIAL' | 'SUCCESS') => void;
  isSendMessageModalOpen: boolean;
  setIsSendMessageModalOpen: (t: boolean) => void;
  isShareModalOpen: boolean;
  setIsShareModalOpen: (t: boolean) => void;
  message: string;
  setMessage: (t: string) => void;
  isAdSaved: boolean;
  setIsAdSaved: (t: boolean) => void;
  isDealerEnquiryModalOpen: boolean;
  setIsDealerEnquiryModalOpen: (t: boolean) => void;
  enquiry?: InitialEnquiryModalValues;
  setEnquiry: (t: InitialEnquiryModalValues) => void;
  isGalleryOpen: boolean;
  setIsGalleryOpen: (t: boolean) => void;
  isYoutubeReviewOpen: boolean;
  setIsYoutubeReviewOpen: (t: boolean) => void;
  isStripeModalOpen: boolean;
  setIsStripeModalOpen: (t: boolean) => void;
  stripeQuantity: number;
  setStripeQuantity: (t: number) => void;
  similarAds: ISearchPageAds[];
  setSimilarAds: (recommendedAds: ISearchPageAds[]) => void;
  isPostEnquirySimilarAdsModalOpen: boolean;
  setIsPostEnquirySimilarAdsModalOpen: (t: boolean) => void;
}

const AdDetailsContext = createContext<AdDetailsContextI>({
  isReportAdModalOpen: false,
  setIsReportAdModalOpen: () => null,
  adId: 0,
  setAdId: () => null,
  categories: [],
  setCategories: () => null,
  currentReportAdView: 'INITIAL',
  setCurrentReportAdView: () => null,
  isSendMessageModalOpen: false,
  setIsSendMessageModalOpen: () => null,
  isShareModalOpen: false,
  setIsShareModalOpen: () => null,
  message: '',
  setMessage: () => null,
  isAdSaved: false,
  setIsAdSaved: () => null,
  isDealerEnquiryModalOpen: false,
  setIsDealerEnquiryModalOpen: () => null,
  enquiry: undefined,
  setEnquiry: () => null,
  isGalleryOpen: false,
  setIsGalleryOpen: () => null,
  isYoutubeReviewOpen: false,
  setIsYoutubeReviewOpen: () => null,
  isStripeModalOpen: false,
  setIsStripeModalOpen: () => null,
  stripeQuantity: 1,
  setStripeQuantity: () => null,
  similarAds: [],
  setSimilarAds: () => null,
  isPostEnquirySimilarAdsModalOpen: false,
  setIsPostEnquirySimilarAdsModalOpen: () => null,
});

function AdDetailsProvider({ children, userSaved }: AdDetailsProviderProps) {
  const [isReportAdModalOpen, setIsReportAdModalOpen] =
    useState<boolean>(false);
  const [adId, setAdId] = useState<number>(0);
  const [categories, setCategories] = useState<category[]>();
  const [currentReportAdView, setCurrentReportAdView] = useState<
    'INITIAL' | 'SUCCESS'
  >('INITIAL');

  const [isSendMessageModalOpen, setIsSendMessageModalOpen] =
    useState<boolean>(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [isAdSaved, setIsAdSaved] = useState<boolean>(userSaved ?? false);
  const [isDealerEnquiryModalOpen, setIsDealerEnquiryModalOpen] =
    useState<boolean>(false);
  const [enquiry, setEnquiry] = useState<InitialEnquiryModalValues | undefined>(
    undefined,
  );
  const [
    isPostEnquirySimilarAdsModalOpen,
    setIsPostEnquirySimilarAdsModalOpen,
  ] = useState<boolean>(false);
  const [isGalleryOpen, setIsGalleryOpen] = useState<boolean>(false);
  const [isYoutubeReviewOpen, setIsYoutubeReviewOpen] =
    useState<boolean>(false);
  const [isStripeModalOpen, setIsStripeModalOpen] = useState<boolean>(false);
  const [stripeQuantity, setStripeQuantity] = useState<number>(1);
  const [similarAds, setSimilarAds] = useState<ISearchPageAds[]>([]);

  return (
    <AdDetailsContext.Provider
      value={{
        isReportAdModalOpen,
        setIsReportAdModalOpen,
        adId,
        setAdId,
        categories,
        setCategories,
        currentReportAdView,
        setCurrentReportAdView,
        isSendMessageModalOpen,
        setIsSendMessageModalOpen,
        isShareModalOpen,
        setIsShareModalOpen,
        message,
        setMessage,
        isAdSaved,
        setIsAdSaved,
        isDealerEnquiryModalOpen,
        setIsDealerEnquiryModalOpen,
        enquiry,
        setEnquiry,
        isGalleryOpen,
        setIsGalleryOpen,
        isYoutubeReviewOpen,
        setIsYoutubeReviewOpen,
        isStripeModalOpen,
        setIsStripeModalOpen,
        stripeQuantity,
        setStripeQuantity,
        similarAds,
        setSimilarAds,
        isPostEnquirySimilarAdsModalOpen,
        setIsPostEnquirySimilarAdsModalOpen,
      }}
    >
      {children}
    </AdDetailsContext.Provider>
  );
}

const useAdDetailsContext = () => useContext(AdDetailsContext);

export { AdDetailsProvider, useAdDetailsContext };
